import { useMemo } from "react";

import dayjs from "dayjs";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
  X,
} from "lucide-react";

import { CitySearchSelect } from "@/components/fields/city-search-select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  CONTENT_TYPE_LABEL,
  type ContentType,
  SOURCE_LABEL,
  type SourceKind,
  type TinaSiteOption,
  countryFlag,
  normalizeCity,
} from "@/pages/tina/runs/components/site-picker/utils";
import type { CityFacet } from "@/pages/tina/sites/hooks/use-site-facets";

export interface RunSourcePickerProps {
  /** The current server page of sites (already filtered by the active scope). */
  rows: TinaSiteOption[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  error: string | null;
  contentType: ContentType;
  source: SourceKind;
  country: string | null;
  countryOptions: string[];
  onCountryChange: (country: string) => void;
  city: string | null;
  /** A pick carries the city's country; null clears the city. */
  onCityChange: (pick: CityFacet | null) => void;
  /** Every city with its country, so a city can be searched without a country. */
  cityOptions: CityFacet[];
  query: string;
  onQueryChange: (query: string) => void;
  selectedNames: string[];
  selected: Set<string>;
  /** Records seen so far (page rows + select-all), for basket rendering. */
  knownSites: Map<string, TinaSiteOption>;
  onToggle: (name: string) => void;
  /** Select the given names (used by the current-page controls). */
  onSelect: (names: string[]) => void;
  /** Select every site in the current content × source × country scope. */
  onSelectAllInScope: () => void;
  onDeselect: (names: string[]) => void;
  onClear: () => void;
}

function formatLastRun(value?: string | null) {
  if (!value) return "—";
  const date = dayjs(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : "—";
}

export function RunSourcePicker({
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  isLoading,
  error,
  contentType,
  source,
  country,
  countryOptions,
  onCountryChange,
  city,
  onCityChange,
  cityOptions,
  query,
  onQueryChange,
  selectedNames,
  selected,
  knownSites,
  onToggle,
  onSelect,
  onSelectAllInScope,
  onDeselect,
  onClear,
}: RunSourcePickerProps) {
  const cityPicks = useMemo(
    () =>
      cityOptions.map((o) => ({ city: o.city, countryCode: o.country_code })),
    [cityOptions],
  );
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageNames = rows.map((site) => site.name);
  const selectedOnPage = pageNames.filter((name) => selected.has(name));
  const headerState: boolean | "indeterminate" =
    pageNames.length > 0 && selectedOnPage.length === pageNames.length
      ? true
      : selectedOnPage.length > 0
        ? "indeterminate"
        : false;

  const togglePage = () => {
    if (selectedOnPage.length === pageNames.length) onDeselect(pageNames);
    else onSelect(pageNames);
  };

  const scopeLabel = `${CONTENT_TYPE_LABEL[contentType]} · ${SOURCE_LABEL[source]}${
    country ? ` · ${country}` : ""
  }`;

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      {/* ── Sources ─────────────────────────────────────────── */}
      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="flex flex-col gap-3 border-b p-3">
          <div className="grid gap-2 sm:grid-cols-[180px_180px_minmax(0,1fr)]">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium">Country</label>
              <Select
                value={country ?? undefined}
                onValueChange={onCountryChange}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countryOptions.map((code) => {
                    const flag = countryFlag(code);
                    return (
                      <SelectItem key={code} value={code}>
                        {flag ? <span className="mr-1.5">{flag}</span> : null}
                        {code}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium">
                City{" "}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </label>
              <CitySearchSelect
                options={cityPicks}
                city={city}
                country={country}
                onChange={(pick) =>
                  onCityChange(
                    pick && { city: pick.city, country_code: pick.countryCode },
                  )
                }
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium">Search</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => onQueryChange(event.target.value)}
                  placeholder={
                    country ? `Filter sites in ${country}…` : "Filter sites…"
                  }
                  className="h-9 pl-8"
                  disabled={!country}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!country || total === 0}
              onClick={onSelectAllInScope}
            >
              Select all{country ? ` in ${country}` : ""}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={rows.length === 0}
              onClick={() => onSelect(pageNames)}
            >
              Select visible
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={selectedNames.length === 0}
              onClick={onClear}
            >
              Clear
            </Button>
            <span className="ml-auto text-xs text-muted-foreground">
              {total} in scope · {selectedNames.length} selected
            </span>
          </div>
        </div>

        {error ? (
          <div className="p-4 text-sm text-destructive">{error}</div>
        ) : !country ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Select a country, or search a city, to choose{" "}
            {SOURCE_LABEL[source].toLowerCase()} for{" "}
            {CONTENT_TYPE_LABEL[contentType]}.
          </div>
        ) : isLoading ? (
          <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading enabled sites…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No {SOURCE_LABEL[source].toLowerCase()} sites match for{" "}
            {CONTENT_TYPE_LABEL[contentType]} in {country}.
          </div>
        ) : (
          <>
            <ScrollArea className="h-[472px]">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        aria-label="Select visible"
                        checked={headerState}
                        onCheckedChange={togglePage}
                      />
                    </TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead className="w-40">City</TableHead>
                    <TableHead className="w-28">Last run</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((site) => {
                    const isSelected = selected.has(site.name);
                    return (
                      <TableRow
                        key={site.name}
                        data-state={isSelected ? "selected" : undefined}
                        className="cursor-pointer"
                        onClick={() => onToggle(site.name)}
                      >
                        <TableCell>
                          {/* Visual-only selection mark — the row's onClick is
                              the real control. NOT a Radix <Checkbox>: inside a
                              <form> (e.g. the groups form) Radix mounts a hidden
                              form input whose BubbleInput dispatches a *bubbling*
                              click whenever `checked` changes. That click bubbles
                              back to this row's onClick and re-toggles forever
                              ("Maximum update depth exceeded"). A plain span has
                              no input and no bubbling click. */}
                          <span
                            aria-hidden
                            data-state={isSelected ? "checked" : "unchecked"}
                            className={cn(
                              "grid h-4 w-4 shrink-0 place-content-center rounded-sm border border-primary",
                              isSelected &&
                                "bg-primary text-primary-foreground",
                            )}
                          >
                            {isSelected ? <Check className="h-4 w-4" /> : null}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">
                          {site.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {normalizeCity(site.city)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatLastRun(site.last_processed_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
            <div className="flex items-center justify-between gap-2 border-t p-2">
              <span className="text-xs text-muted-foreground">
                Page {page} of {pageCount}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => onPageChange(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => onPageChange(page + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Basket ──────────────────────────────────────────── */}
      <div className="self-start overflow-hidden rounded-lg border bg-card">
        <div className="flex items-center justify-between gap-2 border-b p-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">Run basket</h3>
            <p className="truncate text-xs text-muted-foreground">
              {scopeLabel}
            </p>
          </div>
          <Badge variant="secondary">{selectedNames.length}</Badge>
        </div>
        <ScrollArea className="h-[472px]">
          <div className="space-y-2 p-3">
            {selectedNames.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                No sites selected for this run yet.
              </div>
            ) : (
              selectedNames.map((name) => (
                <div
                  key={name}
                  className={cn(
                    "grid grid-cols-[minmax(0,1fr)_28px] items-center gap-2 rounded-md border bg-background p-2",
                  )}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {normalizeCity(knownSites.get(name)?.city)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label={`Remove ${name}`}
                    onClick={() => onDeselect([name])}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
